import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import {
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Spinner,
} from "reactstrap";
import Autosuggest from "react-autosuggest";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";

// app
import { formatAddress } from "../../utils";
import { SUPPORT_EMAIL } from "../../constants";
import {
  AddressNotFound,
  SmallSpinner,
  CountyNote,
  AddressPillWrap,
  SuggestionsDropdown,
} from "./styled";
import "./autosuggest-theme.css";

const CURRENT_STEP = 0;

const LookupAddressForm = ({
  addressSuggestions,
  onSubmit,
  getAddressSuggestions,
  getExternalSuggestions,
  clearSuggestions,
  selectAddress,
  isLoading,
}) => {
  // Requires a Router ancestor (always satisfied: rendered inside
  // FreeAssessmentPage under App.js's <Router>; tests use MemoryRouter).
  // Used to route uncovered-address picks to /coverage.
  const history = useHistory();
  const [address, setAddress] = useState("");
  const selectedSuggestionRef = useRef(null);
  const wrapperRef = useRef(null);
  const [suggestionStarted, setSuggestionStarted] = useState(true);
  const [, repositionTick] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCallSuggestions = useCallback(
    debounce((str) => {
      getAddressSuggestions(str);
      setSuggestionStarted(false);
    }, 400),
    [getAddressSuggestions]
  );

  useEffect(() => {
    return () => handleCallSuggestions.cancel();
  }, [handleCallSuggestions]);

  // Keep the portaled dropdown anchored to the input while it's open.
  useEffect(() => {
    if (!addressSuggestions.length) return undefined;
    const onMove = () => repositionTick((n) => n + 1);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [addressSuggestions.length]);

  const handleChange = (e, { newValue }) => {
    if (newValue !== selectedSuggestionRef.current) {
      selectAddress({});
    }
    setAddress(newValue);
  };

  const onSuggestionFetchRequested = ({ value }) => {
    if (value === address) {
      return;
    }

    if (value.trim() !== "") {
      setSuggestionStarted(true);
      return handleCallSuggestions(value);
    }

    clearSuggestions();
  };

  const getSuggestionValue = (suggestion) => {
    const formatted = formatAddress(suggestion);
    selectedSuggestionRef.current = formatted;
    selectAddress(suggestion);
    // Addresses that only came back from the external geocoder
    // (``covered: false``) aren't in our DB — route to /coverage, which
    // logs the demand, instead of advancing into the report flow.
    if (suggestion.covered === false) {
      history.push("/coverage");
    } else {
      onSubmit(CURRENT_STEP + 1);
    }
    return formatted;
  };

  const renderSuggestion = (suggestion) => (
    <span>{formatAddress(suggestion)}</span>
  );

  // "Search every state" escape hatch — only while showing DB matches, so
  // the user can reach the geocoder even when the DB returned loose matches.
  const showEscapeHatch =
    addressSuggestions.length > 0 &&
    !addressSuggestions.some((s) => s.covered === false);

  const dropdownStyle = () => {
    const el =
      wrapperRef.current &&
      wrapperRef.current.querySelector(".react-autosuggest__input");
    if (!el) return { position: "fixed", visibility: "hidden" };
    const r = el.getBoundingClientRect();
    return { position: "fixed", top: r.bottom + 6, left: r.left, width: r.width, zIndex: 4000 };
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    const node = (
      <SuggestionsDropdown {...containerProps} style={dropdownStyle()}>
        {children}
        {showEscapeHatch && (
          <button
            type="button"
            className="address-escape-hatch"
            onMouseDown={(e) => {
              e.preventDefault();
              const q = address.trim();
              if (q) getExternalSuggestions(q);
            }}
          >
            <i className="fa fa-search" aria-hidden="true" />
            <span>
              Don't see your address? <strong>Search every state →</strong>
            </span>
          </button>
        )}
      </SuggestionsDropdown>
    );
    // Portal to body so the dropdown escapes ancestor clip/stacking contexts.
    return typeof document !== "undefined"
      ? ReactDOM.createPortal(node, document.body)
      : node;
  };

  const renderAddressNotFound = () => {
    if (
      address.trim() !== "" &&
      addressSuggestions.length < 1 &&
      !isLoading &&
      !suggestionStarted
    ) {
      return (
        <AddressNotFound>
          We couldn't find that address. Please enter the full street address, or contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for help.
        </AddressNotFound>
      );
    }
  };

  const inputProps = {
    onChange: handleChange,
    value: address,
    placeholder: "Address",
    type: "address",
    "aria-label": "Enter your property address",
  };

  return (
    <>
      <h4 className="title">
        We need to know your address to get started, enter it below.
      </h4>
      <Form className="register-form" onSubmit={(e) => e.preventDefault()}>
        <AddressPillWrap ref={wrapperRef}>
          <InputGroup className="form-group-no-border">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                {isLoading ? (
                  <SmallSpinner as={Spinner} color="info" />
                ) : (
                  // Bold, simple house silhouette — triangular
                  // roof on a square base.  No rounded-corner
                  // arcs (which read as "circles" at small
                  // sizes) and no door/window cutouts (which
                  // looked like a square inside a square).
                  // Sized 22px so it reads as a clear house
                  // alongside the input text, not a tiny
                  // ambiguous glyph.
                  <svg
                    aria-hidden="true"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 3 2 12h3v8h14v-8h3z" />
                  </svg>
                )}
              </InputGroupText>
            </InputGroupAddon>
            <Autosuggest
              suggestions={addressSuggestions}
              renderSuggestion={renderSuggestion}
              renderSuggestionsContainer={renderSuggestionsContainer}
              onSuggestionsFetchRequested={onSuggestionFetchRequested}
              getSuggestionValue={getSuggestionValue}
              inputProps={inputProps}
              renderInputComponent={(inputProps) => <Input {...inputProps} />}
              alwaysRenderSuggestions
            />
          </InputGroup>
        </AddressPillWrap>
        {renderAddressNotFound()}
        <CountyNote>
          We support single-family homes and condos in <b>Bexar, Collin, Dallas, Denton, Fort Bend, Harris, Hays, Montgomery, Tarrant, Travis,</b> and <b>Williamson</b> counties.
          Having trouble? Email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          .
        </CountyNote>
      </Form>
    </>
  );
};

LookupAddressForm.propTypes = {
  addressSuggestions: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  getAddressSuggestions: PropTypes.func.isRequired,
  getExternalSuggestions: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func.isRequired,
  selectAddress: PropTypes.func.isRequired,
};

export default LookupAddressForm;
